import NoData from 'global/components/alerts/NoData';
import { MiniSwiper } from 'global/components/swipers/MiniSwiper';
import { SportsSeasonContext } from 'projectSpecific/sports/cnr/contexts/SportsSeasonContext';
import { _matchLsTypes } from 'projectSpecific/sports/cnr/reducers/SportsDataReducer';
import { useContext, useMemo, useState } from 'react';
import { _standingsViewTypes, SectionStandingz } from './SectionStandingz';
import StandingsLegend from './StandingsLegend';
import TeamList from '../teams/TeamList';
import DisplayLabels from '../random/DisplayLabels';
import { DropDownSquares } from '../random/DropDownSquares';
import _ from 'lodash';
import PopupLabels from '../random/PopupLabels';
import PopupSelect from '../random/PopupSelect';

const LevelSectionStandings = ({
  _matchKey_page,
  _standingType,
  _teamKey_page,
  _teamsFavs,
  forOverview,
  forPlayoff,
  playoffLevel,
  noData,
  selectedLevelKey,
  showPlayoffPicture,
}) => {

  const sportsSeasonContext = useContext(SportsSeasonContext);
  const { sportsSeason_state } = sportsSeasonContext ?? {};
  const { sportsShowType, matches_info, teams_info, currentSeason_info } = sportsSeason_state ?? {};
  const { teams } = teams_info ?? {}
  const { matches_groupInfo } = matches_info ?? {}
  const { levelKeys } = matches_groupInfo ?? {}
  const { gameScoringType } = currentSeason_info ?? {}

  // get the relevant level keys
  const _levelKeyz = _.difference(levelKeys, [_matchLsTypes.Non, _matchLsTypes.nonDistrict, _matchLsTypes.nonSection])

  // all the matches
  const { matches } = matches_info ?? {}

  const [standingsViewType, setStandingsViewType] = useState(_standingsViewTypes.full)
  const handleStandingsViewType = (svt) => setStandingsViewType(svt)

  const { _levelKeys, _matchSections } = useMemo(() => {

    if (_matchKey_page) {
      return { ...getMatchLevelKeys(matches, _matchKey_page) }
    } else {
      return { _levelKeys: _levelKeyz }
    }

  }, [levelKeys, _matchKey_page])

  const ss = (lk, _matchSections) => <SectionStandingz
    levelKey={lk}
    matchSections={_matchSections}
    _teamsFavs={_teamsFavs}
    teamKey={_teamKey_page}
    matchKey={_matchKey_page}
    forPlayoff={forPlayoff}
    standingsViewType={standingsViewType}
  />

  const standingsLegend = useMemo(() => {
    if (forOverview || forPlayoff) return null;
    return (
      <StandingsLegend />
    );
  }, []);

  const displayLabels = useMemo(() => {
    if (forOverview || forPlayoff) return null;
    return (
      <PopupLabels
        items={_standingsViewTypes}
        currentValue={standingsViewType}
        onClick={handleStandingsViewType}
        title={_.startCase('standingsViewType')}
      />
    );
  }, [standingsViewType]);

  /**
   * 
   * @param {object} lks 
   * @param {object} _matchSections 
   * @returns a group of SectionStandingz with captions
   */
  const levelSectionsContent = (lks, _matchSections) => {
    if (!lks) return null;
    const items = _.map(lks, lk => (
      {
        caption: lk,
        content: ss(lk, _matchSections)
      }
    ))
    return items
  };

  const teamStandings = useMemo(() => {
    if (_teamKey_page && forOverview) {
      return <SectionStandingz
        levelKey={selectedLevelKey}
        _teamsFavs={_teamsFavs}
        teamKey={_teamKey_page}
        matchKey={_matchKey_page}
        forOverview={forOverview}
        forPlayoff={forPlayoff}
        standingsViewType={standingsViewType}
      />
    }
  }, [_teamKey_page, standingsViewType, gameScoringType])

  const leagueStandings = useMemo(() => {

    // if no level keys return
    if (!_levelKeys) { return <div>NLKS</div>; }

    // if this is for a playoff, show that
    if (showPlayoffPicture || playoffLevel) {
      return <SectionStandingz
        levelKey={playoffLevel}
        _teamsFavs={_teamsFavs}
        forPlayoff={forPlayoff}
        standingsViewType={standingsViewType}
      />
    }

    // if this is a match page, show the standings for the 2 teams in the match
    if (_matchKey_page) {
      return <MiniSwiper
        includeButtons={false}
        swipeItems={levelSectionsContent(_levelKeys, _matchSections)}
        showButtons={false}
      />
    }

    if (_teamKey_page && teams?.[_teamKey_page]?.levels) {
      return <MiniSwiper
        includeButtons={false}
        swipeItems={levelSectionsContent([teams?.[_teamKey_page]?.levels], _matchSections)}
        showButtons={false}
      />
    }

    if (noData) {
      return <NoData fullCaption={'No Standings'} />;
    }

    // show the standing for each of the level keys in a swiper (normal)
    return (
      <MiniSwiper
        includeButtons={!forPlayoff}
        swipeItems={levelSectionsContent(_levelKeys)}
        showButtons={true}
        other={standingsLegend}
        topper={displayLabels}
      // topper={teamsDd}
      />
    );
  }, [
    sportsShowType,
    _standingType,
    forOverview,
    playoffLevel,
    showPlayoffPicture,
    selectedLevelKey,
    _matchKey_page,
    _levelKeys,
    _teamKey_page,
    teams,
    standingsViewType,
    gameScoringType,
  ]);

  return forOverview ? teamStandings : leagueStandings;

};

export default LevelSectionStandings;

const getMatchLevelKeys = (matches, _matchKey_page) => {
  const _match = matches?.[_matchKey_page]
  const { away, home } = _match ?? {}
  const { levels: levels_away, sections: sections_away } = away ?? {}
  const { levels: levels_home, sections: sections_home } = home ?? {}
  const _matchLevelKeys = [levels_away]
  const _matchSections = [`${levels_away}-${sections_away}`]
  if (levels_home !== levels_away && !_.includes(_matchLevelKeys, levels_home)) { _matchLevelKeys.push(levels_home) }
  if (sections_home !== sections_away) { _matchSections.push(`${levels_home}-${sections_home}`) }
  return { _levelKeys: _.sortBy(_matchLevelKeys).reverse(), _matchSections }
}